import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "../data/content";
import { scrollToId } from "../hooks/useLenis";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-forest/10 shadow-[0_8px_30px_-18px_rgba(26,79,50,0.35)]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 h-20 flex items-center justify-between">
        <button onClick={() => go("hero")} data-testid="nav-logo" className="focus:outline-none">
          <Logo size={46} />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => go(n.id)}
              data-testid={`nav-link-${n.id}`}
              className="group relative text-sm font-semibold text-forest/80 hover:text-forest transition-colors"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-african transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <button
            onClick={() => go("contact")}
            data-testid="nav-cta-audit"
            className="group inline-flex items-center gap-2 rounded-full bg-forest text-white text-sm font-bold pl-5 pr-4 py-2.5 overflow-hidden relative"
          >
            <span className="absolute inset-0 bg-african translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-[cubic-bezier(.16,1,.3,1)]" />
            <span className="relative">Demander un audit</span>
            <ArrowUpRight className="relative w-4 h-4 transition-transform duration-300 group-hover:rotate-45" />
          </button>
        </nav>

        <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="md:hidden text-forest p-2">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-b border-forest/10 px-6 pb-6 pt-2 flex flex-col gap-4"
            data-testid="nav-mobile-menu"
          >
            {NAV.map((n) => (
              <button key={n.id} onClick={() => go(n.id)} className="text-left text-lg font-heading font-bold text-forest" data-testid={`nav-mobile-link-${n.id}`}>
                {n.label}
              </button>
            ))}
            <button onClick={() => go("contact")} className="rounded-full bg-african text-white font-bold py-3" data-testid="nav-mobile-cta">
              Demander un audit
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
